import styled from '@emotion/styled/macro'
import {
  AutoStack,
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
  Text2,
  TimeAgo,
} from 'components'
import { StatusText } from 'components/StatusText'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { Field, FieldArray, FieldProps } from 'formik'
import { FormikDropdown, FormikTextField } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import {
  ProvingDetailsFerryFormValues,
  ProvingDetailsFlightValues,
  ProvingDetailsLodgingValues,
  ProvingDetailsTrackHotelValues,
  TimePicker,
} from 'forms/ProvingDetailsForm'
import { Box, Flex } from 'grid'
import { canUpdateProving, isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { TFunction } from 'i18next'
import { uniq } from 'lodash'
import {
  BookingStatus,
  FlightType,
  OrderIdDistribution,
  Participant,
  ProvingUserRole,
  TrackHotel,
  User,
  UserRole,
} from 'model'
import {
  DayOfWeek,
  DefaultButton,
  IconButton,
  IDropdownOption,
  ITag,
  Label,
  Link,
  Persona,
  PersonaInitialsColor,
  PersonaSize,
  Stack,
  Text,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { getParticipantTypeDisplayName } from 'views/helpers'
import {
  useAccomodationsQuery,
  useDataQuery,
  useTrackHotelsQuery,
} from 'views/ProvingDetailsView/hooks'
import * as Yup from 'yup'

// const textStyles: React.CSSProperties = {
//   display: 'flex',
//   alignItems: 'center',
//   justifyContent: 'center',
// }

const StyledSectionTitle = styled.div`
  padding: 15px 0 3px 0;
  border-bottom: 1px solid;
`

const getParticipantName = (item: Participant, t: TFunction, site: string) => {
  if (
    (item.unknownPerson &&
      item.unknownPerson.isUnknown &&
      item.unknownPerson.personType === 'user') ||
    (item.unknownPerson &&
      item.unknownPerson.isUnknown &&
      item.unknownPerson.personType === 'external')
  ) {
    return t('model.proving.participant.participantUnknown')
  } else if (
    (!item.person &&
      item.unknownPerson &&
      item.unknownPerson.isUnknown === false &&
      item.unknownPerson.personType === 'user') ||
    (!item.person &&
      item.unknownPerson &&
      item.unknownPerson.isUnknown === false &&
      item.unknownPerson.personType === 'external')
  ) {
    return (
      <>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>Beschäftigungsanteil:</Label>
          <Box py={1}>
            <Text>100%</Text>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>Typ:</Label>
          <Box py={1}>
            <Text>{getParticipantTypeDisplayName(item, t)}</Text>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>{t('field.person.label')}</Label>
          <Box py={1}>
            <Text>{`${item.unknownPerson.surName}, ${item.unknownPerson.firstName}`}</Text>
          </Box>
        </Stack>
      </>
    )
  } else if (
    item.unknownPerson &&
    item.unknownPerson.isUnknown &&
    item.unknownPerson.personType === 'worker'
  ) {
    return item.percentage + '%, ' + item.unknownPerson.department
  } else if (
    item.unknownPerson &&
    item.unknownPerson.isUnknown &&
    item.unknownPerson.personType === 'supplier'
  ) {
    return (
      <>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>Beschäftigungsanteil:</Label>
          <Box py={1}>
            <Text>100%</Text>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>Typ:</Label>
          <Box py={1}>
            <Text>{getParticipantTypeDisplayName(item, t)}</Text>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>{t('field.person.label')}:</Label>
          <Box py={1}>
            <Text>
              {item.unknownPerson.supplier
                ? `${item.unknownPerson.surName}, ${item.unknownPerson.firstName} - ${item.unknownPerson.supplier} - ${item.unknownPerson.email}`
                : '[Teilnehmer noch unbekannt]'}
            </Text>
          </Box>
        </Stack>
      </>
    )
  } else if (
    item.unknownPerson &&
    !item.unknownPerson.isUnknown &&
    item.unknownPerson.personType === 'worker'
  ) {
    return (
      <>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>Beschäftigungsanteil:</Label>
          <Box py={1}>
            <Text>{item.percentage}%</Text>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>Typ:</Label>
          <Box py={1}>
            <Text>{getParticipantTypeDisplayName(item, t)}</Text>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>{t('field.person.label')}:</Label>
          <Box py={1}>
            <Link
              href={site}
              target="_blank"
              title="Zur Person"
              onClick={() => {
                localStorage.setItem(
                  'currentPerson',
                  JSON.stringify(item.person)
                )
              }}
            >
              {item.person!.name}
            </Link>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>{t('P-Nummer')}:</Label>
          <Box py={1}>
            <Text>{item.person!._id}</Text>
          </Box>
        </Stack>
      </>
    )
  } else {
    return (
      <>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>Beschäftigungsanteil:</Label>
          <Box py={1}>
            <Text>100%</Text>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>Typ:</Label>
          <Box py={1}>
            <Text>{getParticipantTypeDisplayName(item, t)}</Text>
          </Box>
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>{t('field.person.label')}:</Label>
          <Box py={1}>
            <Link
              href={site}
              target="_blank"
              title="Zur Person"
              onClick={() => {
                localStorage.setItem(
                  'currentPerson',
                  JSON.stringify(item.person)
                )
              }}
            >
              {item.person!.name}
            </Link>
          </Box>
          {/* <Box py={1}>
            <Text> {item.person!.name}</Text>
          </Box> */}
        </Stack>
        <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
          <Label>{t('P-Nummer')}:</Label>
          <Box py={1}>
            <Text> {item.person!.employeeId}</Text>
          </Box>
        </Stack>
      </>
    )
  }
}

export class ParticipantDetailsFormSchema {
  constructor(provingDateFrom: string, provingDateTo: string) {
    const hotelArrayValidation = Yup.array().of(
      Yup.object().test(
        'isValid',
        'Bitte beheben Sie die Fehler im Formular',
        (trackHotel: TrackHotel) => {
          const { hotel, dateFrom, dateTo } = trackHotel
          if (!hotel && !dateFrom && !dateTo) {
            return true
          }
          if (hotel && dateFrom && dateTo) {
            if (dateTo >= dateFrom) {
              return true
            }
          }

          return false
        }
      )
    )
    return Yup.object().shape({
      orderId: Yup.string(),
      trackHotels: Yup.object().shape({
        outward: hotelArrayValidation,
        return: hotelArrayValidation,
      }),
    })
  }
}

export const ParticipantDetailsForm: React.FC = () => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const formikContext = useCurrentFormik()
  const initialValues = formikContext ? formikContext.initialValues : undefined
  const [selectedCarType, setSelectedCarType] = React.useState()
  const [selectedAccomodation, setSelectedAccomodation] = React.useState(
    initialValues.accomodation.accomodationType
  )
  const [, setSelectedFerryType] = React.useState()

  const [selectedFlightType, setSelectedFlightType] = React.useState(
    initialValues.flight.flightType
  )

  const accomodationsQuery = useAccomodationsQuery({
    variables: {
      provingGroundId:
        proving && proving.provingGround && proving.provingGround._id
          ? proving.provingGround._id
          : initialValues.provingGroundId,
    },
  })

  const trackHotelsQuery = useTrackHotelsQuery({
    variables: {
      provingGroundId:
        proving && proving.provingGround && proving.provingGround._id
          ? proving.provingGround._id
          : initialValues.provingGroundId,
    },
  })

  const ferryQuery = useDataQuery({
    variables: {
      provingGroundId:
        proving && proving.provingGround && proving.provingGround._id
          ? proving.provingGround._id
          : initialValues.provingGroundId,
    },
  })
  const pageGeneral =
    window.location.origin + `/provings/${proving.id}?page=general`

  const pagePersonByName = window.location.origin + `/persons/personsByName`

  const pageProvingGround =
    window.location.origin + '/_admin/data/proving-grounds'

  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager =
    auth.hasUserRole(UserRole.ResourceManager) ||
    isInProvingRole(proving, ProvingUserRole.ResourceManager)
  const ferryConnections =
    ferryQuery.data && ferryQuery.data.resources && ferryQuery.data.resources
      ? ferryQuery.data.resources
      : []
  const ferryConnectionsOutward = ferryConnections.filter(
    (ferryConnection) => ferryConnection.type === 'outward'
  )
  const ferryConnectionsReturn = ferryConnections.filter(
    (ferryConnection) => ferryConnection.type === 'return'
  )

  const hotels: ITag[] =
    trackHotelsQuery.data && !trackHotelsQuery.loading
      ? trackHotelsQuery.data.resources.map((resource) => ({
          key: resource._id,
          name: `${resource.title} / ${resource.place} / ${resource.land}`,
        }))
      : []

  const carTypeOptions: IDropdownOption[] = [
    { key: 'none', text: 'Kein Mietwagen' },
    { key: 'compact', text: t('dropdown.option.ctSmall') },
    { key: 'midSize', text: t('dropdown.option.ctMedium') },
    { key: 'fullSize', text: t('dropdown.option.ctLarge') },
    { key: 'estate', text: t('dropdown.option.ctCombi') },
    { key: 'minibus', text: t('dropdown.option.ctMinibus') },
  ]

  const statusOptions: IDropdownOption[] = [
    {
      key: 'notRequired',
      text: t('dropdown.option.trackHotels.status.notRequired'),
    },
    {
      key: 'open',
      text: t('dropdown.option.trackHotels.status.open'),
    },
    {
      key: 'workInProgress',
      text: t('dropdown.option.trackHotels.status.workInProgress'),
    },
    {
      key: 'finished',
      text: t('dropdown.option.trackHotels.status.finished'),
    },
  ]

  const options: IDropdownOption[] = [
    { key: 'roundtrip', text: t('dropdown.option.fOutwardAndReturn') },
    { key: 'outboundFlight', text: t('dropdown.option.fOutward') },
    { key: 'returnFlight', text: t('dropdown.option.fReturn') },
    { key: 'noFlight', text: t('dropdown.option.fNone') },
  ]

  const accomodationOptions: IDropdownOption[] = [
    { key: 'noAccomodation', text: t('dropdown.option.noAccomodation') },
    {
      key: 'accomodationWithPreference',
      text: t('dropdown.option.accomodationWithPreference'),
    },
  ]

  const resetOutward = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('ferry.outward', undefined)
    fieldProps.form.setFieldValue('ferry.dateFrom', undefined)
    fieldProps.form.setFieldValue('ferry.timeFrom', undefined)
  }

  const resetReturn = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('ferry.return', undefined)
    fieldProps.form.setFieldValue('ferry.dateTo', undefined)
    fieldProps.form.setFieldValue('ferry.timeTo', undefined)
  }

  const setOutward = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('ferry.dateFrom', proving.dateFrom)
    fieldProps.form.setFieldValue('ferry.timeFrom', '00:00')
  }

  const setReturn = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('ferry.dateTo', proving.dateTo)
    fieldProps.form.setFieldValue('ferry.timeTo', '00:00')
  }

  const resetForm = (fieldProps: FieldProps<any>) => {
    fieldProps.form.setFieldValue('rentalCar.dateFrom', undefined)
    fieldProps.form.setFieldValue('rentalCar.dateTo', undefined)
  }

  // const validateDateFrom = () => {
  //   return new Date(proving.dateFrom)
  // }

  // const validateDateTo = () => {
  //   return new Date(proving.dateTo)
  // }

  const orderIds = proving.orderIdDistribution!.map(
    (x: OrderIdDistribution) => x
  )

  const accomodations: ITag[] =
    accomodationsQuery.data && !accomodationsQuery.loading
      ? accomodationsQuery.data.resources.map((resource) => ({
          key: resource._id,
          name: resource.title,
        }))
      : []

  let canUpdateCurrentProving = canUpdateProving(proving)
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )

  const isProvingResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const isExternal = auth.hasUserRole(UserRole.Hrg)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  if (isServiceTeam === true) {
    canUpdateCurrentProving = isServiceTeam
  }
  const isProvingEditor = isInProvingRole(proving, ProvingUserRole.Editor)

  const canUpdateStatus = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (
      isAdmin ||
      isProvingResourceManager ||
      isProvingEditor ||
      isServiceTeam ||
      isExternal
    ) {
      return true
    } else {
      return false
    }
  }

  const userCanUpdate = () => {
    if (isMaintenanceHead) {
      return false
    } else if (canUpdateCurrentProving) {
      return true
    } else if (
      isProvingManager &&
      (proving.status === 1 ||
        proving.status === 4 ||
        proving.status === 5 ||
        proving.status === 10)
    ) {
      return true
    } else {
      return false
    }
  }
  const { data } = useDataQuery({
    variables: { provingGroundId: proving!.provingGround!._id },
  })
  let maintenanceHeads: User[] = []
  const maintenanceAreas: string[] = []
  if (initialValues.unknownPerson && initialValues.unknownPerson.department) {
    maintenanceAreas.push(initialValues.unknownPerson.department)
    if (data && data.maintenanceAreas) {
      data.maintenanceAreas.forEach((a: any) => {
        if (maintenanceAreas.includes(a.maintenanceArea)) {
          a.maintenanceHead.map((amh: any) => maintenanceHeads.push(amh))
        }
      })
    }
    maintenanceHeads = uniq(maintenanceHeads)
  }

  return (
    <>
      <FormStack>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.provingTitle.label')}
          </Text>
        </StyledSectionTitle>
        <>
          <Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingTitle.label')}:</Label>
              <Box py={1}>
                <Link href={pageGeneral} target="_blank" title="Zur Erprobung">
                  {`${proving.title}, ${proving.id}`}
                </Link>
              </Box>
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingGroundTitle.label')}:</Label>
              <Box py={1}>
                {isAdmin || isResourceManager ? (
                  <Link
                    href={pageProvingGround}
                    target="_blank"
                    title="Zum Standort"
                    onClick={() => {
                      localStorage.setItem(
                        'currentProvingGround',
                        JSON.stringify(proving.provingGround)
                      )
                    }}
                  >
                    {proving.provingGround ? proving.provingGround.title : ''}
                  </Link>
                ) : (
                  <Text>
                    {proving.provingGround ? proving.provingGround.title : ''}
                  </Text>
                )}
              </Box>
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingStatus.label')}:</Label>
              <Box py={1}>
                <StatusText status={proving!.status || 0} />
              </Box>
            </Stack>
          </Stack>
          {proving.responsible && proving.responsible.length > 0 && (
            <Stack.Item className="composite-field">
              <Label>{t('field.responsible.label')}</Label>
              <Box py={1}>
                <AutoStack horizontal={true} tokens={{ childrenGap: 12 }}>
                  {proving.responsible && proving.responsible.length > 0
                    ? proving.responsible.map((user: User, i: any) => {
                        return <Text key={i}>{user.name}</Text>
                      })
                    : ''}
                </AutoStack>
              </Box>
            </Stack.Item>
          )}
        </>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.personBooking.label')}
          </Text>
        </StyledSectionTitle>
        <>
          {initialValues && (
            <>{getParticipantName(initialValues, t, pagePersonByName)}</>
          )}
          <AutoStack tokens={{ childrenGap: 12 }}>
            <Stack tokens={{ childrenGap: 36 }}>
              <FieldStack horizontal={true}>
                <FieldStackItem
                  name="dateFrom"
                  label={t('field.dateFrom.label')}
                  isRequired={true}
                  component={FormikGraphQLDatePicker}
                  // minDate={new Date(proving.dateFrom)}
                  // maxDate={new Date(proving.dateTo)}
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate}
                  disabled={!userCanUpdate()}
                  initialPickerDate={new Date(proving.dateFrom)}
                />
                <FieldStackItem
                  name="dateTo"
                  label={t('field.dateTo.label')}
                  isRequired={true}
                  component={FormikGraphQLDatePicker}
                  // minDate={new Date(proving.dateFrom)}
                  // maxDate={new Date(proving.dateTo)}
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate}
                  disabled={!userCanUpdate()}
                  initialPickerDate={new Date(proving.dateTo)}
                />
              </FieldStack>
            </Stack>
            <Field
              render={(fieldProps: FieldProps<any>) => (
                <DefaultButton
                  text={t('field.resetDate.label')}
                  onClick={() => {
                    fieldProps.form.setFieldValue('dateFrom', proving.dateFrom)
                    fieldProps.form.setFieldValue('dateTo', proving.dateTo)
                  }}
                  disabled={!userCanUpdate()}
                />
              )}
            />
          </AutoStack>
        </>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.orderId.label')}
          </Text>
        </StyledSectionTitle>
        <>
          <FieldStack>
            <FieldStackItem
              name="orderId"
              label={t('field.orderId.label')}
              isRequired={true}
              render={(fieldProps) => (
                <FormikDropdown
                  disabled={!userCanUpdate()}
                  {...fieldProps}
                  options={[
                    { key: '', text: t('field.orderId.noOrderId') },
                    ...orderIds.map(
                      (orderId) =>
                        ({
                          key: orderId.orderId,
                          text: `${orderId.orderId} - ${
                            orderId.annotation ? `${orderId.annotation} ` : ''
                          }(${orderId.percentage} %)`,
                        } as IDropdownOption)
                    ),
                  ]}
                />
              )}
            />
          </FieldStack>
        </>
        {initialValues.unknownPerson &&
          initialValues.unknownPerson.personType === 'worker' && (
            <>
              <StyledSectionTitle>
                <Text variant="xLarge" as="h2">
                  {t('Werkstattmitarbeiter - Meisterei')}
                </Text>
              </StyledSectionTitle>
              <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
                <Label>{t('Meisterei')}:</Label>
                <Box py={1}>
                  <Text>
                    {initialValues.unknownPerson
                      ? initialValues.unknownPerson.department
                      : ''}
                  </Text>
                </Box>
              </Stack>
              <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
                <Label>{t('Meister')}:</Label>
                <Box py={1}>
                  <Flex>
                    {maintenanceHeads.map((mh) => {
                      return (
                        <Persona
                          key={mh._id}
                          text={mh.name}
                          secondaryText={mh.department! || mh.email!}
                          size={PersonaSize.size40}
                          initialsColor={PersonaInitialsColor.lightRed}
                        />
                      )
                    })}
                  </Flex>
                </Box>
              </Stack>
            </>
          )}

        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            Anmerkungen Admin
          </Text>
        </StyledSectionTitle>
        <>
          <Field
            name="note"
            multiline={true}
            component={FormikTextField}
            disabled={!userCanUpdate()}
          />
        </>
        {proving &&
          proving.provingGround &&
          !proving.provingGround.hideFlight && (
            <>
              <StyledSectionTitle>
                <Text variant="xLarge" as="h2">
                  {t('field.flight.label')}
                </Text>
              </StyledSectionTitle>
              <>
                <FieldStack horizontal={true}>
                  <FieldStackItem
                    name="flight.flightType"
                    render={(
                      fieldProps: FieldProps<ProvingDetailsFlightValues>
                    ) => (
                      <FormikDropdown
                        {...fieldProps}
                        label={t('field.flight.label')}
                        required={true}
                        options={options}
                        onChanged={(option: any) => {
                          setSelectedFlightType(option.key)
                          if (option.key === 'noFlight') {
                            fieldProps.form.setFieldValue(
                              'flight.outboundData',
                              null
                            )
                            fieldProps.form.setFieldValue(
                              'flight.returnData',
                              null
                            )
                          } else if (option.key === 'outboundFlight') {
                            fieldProps.form.setFieldValue(
                              'flight.outboundData.departure',
                              proving.dateFrom
                            )
                            fieldProps.form.setFieldValue(
                              'flight.returnData',
                              null
                            )
                          } else if (option.key === 'returnFlight') {
                            fieldProps.form.setFieldValue(
                              'flight.returnData.departure',
                              proving.dateTo
                            )
                            fieldProps.form.setFieldValue(
                              'flight.outboundData',
                              null
                            )
                          } else if (option.key === 'roundtrip') {
                            fieldProps.form.setFieldValue(
                              'flight.outboundData.departure',
                              proving.dateFrom
                            )
                            fieldProps.form.setFieldValue(
                              'flight.returnData.departure',
                              proving.dateTo
                            )
                          }
                        }}
                        disabled={!userCanUpdate()}
                      />
                    )}
                  />
                  {selectedFlightType !== FlightType.NoFlight && (
                    <FieldStackItem
                      name={`flight.bookingStatus`}
                      render={(fieldProps: FieldProps<any>) => (
                        <FormikDropdown
                          label={t('field.bookingStatus.label')}
                          {...fieldProps}
                          options={statusOptions}
                          disabled={!canUpdateStatus()}
                        />
                      )}
                    />
                  )}
                </FieldStack>
                <FieldStack>
                  <Field
                    name="flight.note"
                    label={t('section.proving.field.note')}
                    multiline={true}
                    disabled={!userCanUpdate()}
                    component={FormikTextField}
                  />
                </FieldStack>
                {(selectedFlightType === FlightType.Roundtrip ||
                  selectedFlightType === FlightType.OutboundFlight) && (
                  <>
                    <Label>{t('field.flight.types.outbound')}</Label>
                    <AutoStack horizontal={true} tokens={{ childrenGap: 12 }}>
                      <Field
                        name="flight.outboundData.airline"
                        label={t('field.flight.airline')}
                        multiline={false}
                        disabled={!userCanUpdate()}
                        component={FormikTextField}
                      />
                      <Field
                        name="flight.outboundData.flightNumber"
                        label={t('field.flight.flightNumber')}
                        multiline={false}
                        disabled={!userCanUpdate()}
                        component={FormikTextField}
                      />
                      <FieldStackItem
                        name="flight.outboundData.departure"
                        label={t('field.flight.departure')}
                        value={proving.dateFrom}
                        component={FormikGraphQLDatePicker}
                        // minDate={new Date(proving.dateFrom)}
                        // maxDate={new Date(proving.dateTo)}
                        firstDayOfWeek={DayOfWeek.Monday}
                        strings={DayPickerStrings}
                        showWeekNumbers={true}
                        disabled={!userCanUpdate()}
                        formatDate={onFormatDate}
                        initialPickerDate={new Date(proving.dateFrom)}
                      />
                      <Stack>
                        <Label>{t('field.flight.departureTime')}</Label>
                        <Stack.Item>
                          <TimePicker
                            component="input"
                            type="time"
                            name="flight.outboundData.departureTime"
                            disabled={!userCanUpdate()}
                          />
                        </Stack.Item>
                      </Stack>
                    </AutoStack>
                  </>
                )}

                {(selectedFlightType === FlightType.Roundtrip ||
                  selectedFlightType === FlightType.ReturnFlight) && (
                  <>
                    <Label>{t('field.flight.types.return')}</Label>
                    <AutoStack horizontal={true} tokens={{ childrenGap: 12 }}>
                      <Field
                        name="flight.returnData.airline"
                        label={t('field.flight.airline')}
                        multiline={false}
                        disabled={!userCanUpdate()}
                        component={FormikTextField}
                      />
                      <Field
                        name="flight.returnData.flightNumber"
                        label={t('field.flight.flightNumber')}
                        multiline={false}
                        disabled={!userCanUpdate()}
                        component={FormikTextField}
                      />
                      <FieldStackItem
                        name="flight.returnData.departure"
                        label={t('field.flight.departure')}
                        value={proving.dateFrom}
                        component={FormikGraphQLDatePicker}
                        // minDate={new Date(proving.dateFrom)}
                        // maxDate={new Date(proving.dateTo)}
                        firstDayOfWeek={DayOfWeek.Monday}
                        strings={DayPickerStrings}
                        showWeekNumbers={true}
                        disabled={!userCanUpdate()}
                        formatDate={onFormatDate}
                        initialPickerDate={new Date(proving.dateFrom)}
                      />
                      <Stack>
                        <Label>{t('field.flight.departureTime')}</Label>
                        <Stack.Item>
                          <TimePicker
                            component="input"
                            type="time"
                            disabled={!userCanUpdate()}
                            name="flight.returnData.departureTime"
                          />
                        </Stack.Item>
                      </Stack>
                    </AutoStack>
                  </>
                )}
              </>
            </>
          )}
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.accomodation.label')}
          </Text>
        </StyledSectionTitle>
        <>
          <FieldStack horizontal={true}>
            <FieldStackItem
              name="accomodation.accomodationType"
              isRequired={true}
              render={(fieldProps: FieldProps<ProvingDetailsLodgingValues>) => {
                return (
                  <FormikDropdown
                    label={t('field.accomodation.label')}
                    onChanged={(option: any) => {
                      setSelectedAccomodation(option.key)
                      if (option.key === 'noAccomodation') {
                        fieldProps.form.setFieldValue(
                          'accomodation.accomodation',
                          null
                        )
                      } else {
                        fieldProps.form.setFieldValue(
                          'accomodation.dateFrom',
                          proving.dateFrom
                        )
                        fieldProps.form.setFieldValue(
                          'accomodation.dateTo',
                          proving.dateTo
                        )
                      }
                    }}
                    {...fieldProps}
                    disabled={!userCanUpdate()}
                    options={accomodationOptions}
                  />
                )
              }}
            />
            {selectedAccomodation !== 'noAccomodation' && (
              <FieldStackItem
                name={`accomodation.bookingStatus`}
                render={(fieldProps: FieldProps<any>) => (
                  <FormikDropdown
                    label={t('field.bookingStatus.label')}
                    {...fieldProps}
                    disabled={!canUpdateStatus()}
                    options={statusOptions}
                  />
                )}
              />
            )}
          </FieldStack>

          <FlexSpinner
            loading={accomodationsQuery.loading}
            error={accomodationsQuery.error}
          >
            {selectedAccomodation &&
              selectedAccomodation === 'accomodationWithPreference' && (
                <FieldStack horizontal={true}>
                  <FieldStackItem
                    name="accomodation.accomodation"
                    render={(
                      fieldProps: FieldProps<ProvingDetailsLodgingValues>
                    ) => {
                      return (
                        <FormikTagPicker
                          label={t('field.accomodation.setAccomodation.label')}
                          {...fieldProps}
                          disabled={!userCanUpdate()}
                          tags={accomodations}
                        />
                      )
                    }}
                  />
                </FieldStack>
              )}
            {['noPreference', 'accomodationWithPreference'].includes(
              selectedAccomodation
            ) && (
              <>
                <FieldStack horizontal={true}>
                  <FieldStackItem
                    name="accomodation.dateFrom"
                    label={t('field.dateFrom.label')}
                    value={proving.dateFrom}
                    component={FormikGraphQLDatePicker}
                    // minDate={new Date(proving.dateFrom)}
                    // maxDate={new Date(proving.dateTo)}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={DayPickerStrings}
                    showWeekNumbers={true}
                    disabled={!userCanUpdate()}
                    formatDate={onFormatDate}
                    initialPickerDate={new Date(proving.dateFrom)}
                  />
                  <FieldStackItem
                    name="accomodation.dateTo"
                    label={t('field.dateTo.label')}
                    value={proving.dateTo}
                    component={FormikGraphQLDatePicker}
                    // minDate={new Date(proving.dateFrom)}
                    // maxDate={new Date(proving.dateTo)}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={DayPickerStrings}
                    showWeekNumbers={true}
                    disabled={!userCanUpdate()}
                    formatDate={onFormatDate}
                    initialPickerDate={new Date(proving.dateFrom)}
                  />
                </FieldStack>
                <FieldStack>
                  <Field
                    name="accomodation.note"
                    label={t('section.proving.field.note')}
                    multiline={true}
                    disabled={!userCanUpdate()}
                    component={FormikTextField}
                  />
                </FieldStack>
              </>
            )}
          </FlexSpinner>
        </>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.accomodation.additionalAccomodation')}
          </Text>
        </StyledSectionTitle>
        <>
          <FieldStack>
            <Field
              name="accomodation.additionalAccomodation"
              label={t('section.proving.field.note')}
              multiline={true}
              disabled={!userCanUpdate()}
              component={FormikTextField}
            />
          </FieldStack>
        </>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.rentalCar.label')}
          </Text>
        </StyledSectionTitle>
        <>
          <FieldStack horizontal={true}>
            <FieldStackItem
              name="rentalCar.carType"
              render={(fieldProps) => {
                setSelectedCarType(fieldProps.field.value)
                return (
                  <FormikDropdown
                    required={true}
                    label={t('field.carType.label')}
                    {...fieldProps}
                    disabled={!userCanUpdate()}
                    options={carTypeOptions}
                    onChanged={(option) => {
                      if (option.key === 'none') {
                        setSelectedCarType(option.key)
                        resetForm(fieldProps)
                      } else {
                        setSelectedCarType(option.key)
                        fieldProps.form.setFieldValue(
                          'rentalCar.dateFrom',
                          proving.dateFrom
                        )
                        fieldProps.form.setFieldValue(
                          'rentalCar.dateTo',
                          proving.dateTo
                        )
                      }
                    }}
                  />
                )
              }}
            />
            {selectedCarType !== 'none' && (
              <FieldStackItem
                name={`rentalCar.bookingStatus`}
                render={(fieldProps: FieldProps<any>) => (
                  <FormikDropdown
                    label={t('field.bookingStatus.label')}
                    {...fieldProps}
                    disabled={!canUpdateStatus()}
                    options={statusOptions}
                  />
                )}
              />
            )}
          </FieldStack>

          {selectedCarType !== 'none' && (
            <>
              <FieldStack horizontal={true}>
                <FieldStackItem
                  required={true}
                  name="rentalCar.dateFrom"
                  label={t('field.dateFrom.label')}
                  component={FormikGraphQLDatePicker}
                  // minDate={validateDateFrom()}
                  // maxDate={validateDateTo()}
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate}
                  disabled={!userCanUpdate()}
                  initialPickerDate={new Date(proving.dateFrom)}
                />
                <FieldStackItem
                  required={true}
                  name="rentalCar.dateTo"
                  label={t('field.dateTo.label')}
                  component={FormikGraphQLDatePicker}
                  // minDate={validateDateFrom()}
                  // maxDate={validateDateTo()}
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate}
                  disabled={!userCanUpdate()}
                  initialPickerDate={new Date(proving.dateFrom)}
                />
              </FieldStack>
              <FieldStack>
                <Field
                  name="rentalCar.note"
                  label={t('section.proving.field.note')}
                  multiline={true}
                  component={FormikTextField}
                  disabled={!userCanUpdate()}
                />
              </FieldStack>
            </>
          )}
        </>
        {proving &&
          proving.provingGround &&
          !proving.provingGround.hideHotels && (
            <>
              <StyledSectionTitle>
                <Text variant="xLarge" as="h2">
                  {t('field.trackHotel.return.label')}
                </Text>
              </StyledSectionTitle>
              <FlexSpinner
                loading={trackHotelsQuery.loading}
                error={trackHotelsQuery.error}
              >
                <Label>{t('field.trackHotel.outward.header')}</Label>
                <FieldArray name="trackHotels.outward">
                  {({ form, name, ...helpers }) => {
                    const values: {
                      hotel: string
                      dateFrom: Date
                      dateTo: Date
                    }[] = form.values.trackHotels.outward

                    return (
                      <AutoStack tokens={{ childrenGap: 12 }}>
                        {values.map((x: any, i: number) => (
                          <FieldStack key={x._id || x.id || i}>
                            <FieldStack horizontal={true}>
                              <FieldStackItem
                                name={`trackHotels.outward[${i}].hotel`}
                                render={(
                                  fieldProps: FieldProps<ProvingDetailsTrackHotelValues>
                                ) => {
                                  return (
                                    <FormikTagPicker
                                      label={t(
                                        'field.trackHotel.outward.tagPicker'
                                      )}
                                      {...fieldProps}
                                      tags={hotels}
                                      pickerCalloutProps={{ calloutWidth: 400 }}
                                      disabled={!userCanUpdate()}
                                    />
                                  )
                                }}
                              />
                              <FieldStackItem
                                name={`trackHotels.outward[${i}].dateFrom`}
                                render={(fieldProps) => {
                                  return (
                                    <FormikGraphQLDatePicker
                                      label={t(
                                        'field.trackHotel.dateFrom.label'
                                      )}
                                      {...fieldProps}
                                      // minDate={validateDateFrom()}
                                      // maxDate={validateDateTo()}
                                      firstDayOfWeek={DayOfWeek.Monday}
                                      strings={DayPickerStrings}
                                      showWeekNumbers={true}
                                      formatDate={onFormatDate as any}
                                      disabled={!userCanUpdate()}
                                      initialPickerDate={
                                        new Date(proving.dateFrom)
                                      }
                                    />
                                  )
                                }}
                              />
                              <FieldStackItem
                                name={`trackHotels.outward[${i}].dateTo`}
                                render={(fieldProps) => (
                                  <FormikGraphQLDatePicker
                                    label={t('field.trackHotel.dateTo.label')}
                                    {...fieldProps}
                                    // minDate={validateDateFrom()}
                                    // maxDate={validateDateTo()}
                                    firstDayOfWeek={DayOfWeek.Monday}
                                    strings={DayPickerStrings}
                                    showWeekNumbers={true}
                                    formatDate={onFormatDate as any}
                                    disabled={!userCanUpdate()}
                                    initialPickerDate={
                                      new Date(proving.dateFrom)
                                    }
                                  />
                                )}
                              />
                              <FieldStackItem
                                name={`trackHotels.outward.${i}.bookingStatus`}
                                render={(fieldProps: FieldProps<any>) => (
                                  <FormikDropdown
                                    label={t('field.bookingStatus.label')}
                                    {...fieldProps}
                                    options={statusOptions}
                                    disabled={!canUpdateStatus()}
                                  />
                                )}
                              />
                              <IconButton
                                styles={{
                                  root: { color: 'red', marginTop: 'auto' },
                                }}
                                disabled={!userCanUpdate()}
                                iconProps={{ iconName: 'Delete' }}
                                onClick={() => helpers.remove(i)}
                              />
                            </FieldStack>
                            <FieldStack>
                              <FieldStackItem
                                name={`trackHotels.outward[${i}].note`}
                                label={t('section.proving.field.note')}
                                multiline={true}
                                component={FormikTextField}
                                disabled={!userCanUpdate()}
                              />
                            </FieldStack>
                          </FieldStack>
                        ))}
                        <FieldStack horizontal={true}>
                          <DefaultButton
                            text={'+ Hotel Hin'}
                            onClick={() => {
                              if (form.values.trackHotels.outward.length < 10) {
                                helpers.push({
                                  hotel: '',
                                  dateFrom: '',
                                  dateTo: '',
                                  bookingStatus: BookingStatus.Open,
                                  note: '',
                                })
                              }
                            }}
                            disabled={
                              form.values.trackHotels.outward.length >= 10 ||
                              !userCanUpdate()
                            }
                          />
                        </FieldStack>
                      </AutoStack>
                    )
                  }}
                </FieldArray>
                <Label>{t('field.trackHotel.return.header')}</Label>
                <FieldArray name="trackHotels.return">
                  {({ form, name, ...helpers }) => {
                    const values: {
                      hotel: string
                      dateFrom: Date
                      dateTo: Date
                    }[] = form.values.trackHotels.return

                    return (
                      <AutoStack tokens={{ childrenGap: 12 }}>
                        {values.map((x: any, i: number) => (
                          <FieldStack key={x._id || x.id || i}>
                            <FieldStack horizontal={true}>
                              <FieldStackItem
                                name={`trackHotels.return[${i}].hotel`}
                                render={(
                                  fieldProps: FieldProps<ProvingDetailsTrackHotelValues>
                                ) => (
                                  <FormikTagPicker
                                    label={t(
                                      'field.trackHotel.return.tagPicker'
                                    )}
                                    {...fieldProps}
                                    tags={hotels}
                                    pickerCalloutProps={{ calloutWidth: 400 }}
                                    disabled={!userCanUpdate()}
                                  />
                                )}
                              />
                              <FieldStackItem
                                name={`trackHotels.return[${i}].dateFrom`}
                                render={(fieldProps) => (
                                  <FormikGraphQLDatePicker
                                    label={t('field.trackHotel.dateFrom.label')}
                                    {...fieldProps}
                                    // minDate={validateDateFrom()}
                                    // maxDate={validateDateTo()}
                                    firstDayOfWeek={DayOfWeek.Monday}
                                    strings={DayPickerStrings}
                                    showWeekNumbers={true}
                                    formatDate={onFormatDate as any}
                                    disabled={!userCanUpdate()}
                                    initialPickerDate={
                                      new Date(proving.dateFrom)
                                    }
                                  />
                                )}
                              />
                              <FieldStackItem
                                name={`trackHotels.return[${i}].dateTo`}
                                render={(fieldProps) => (
                                  <FormikGraphQLDatePicker
                                    label={t('field.trackHotel.dateTo.label')}
                                    {...fieldProps}
                                    // minDate={validateDateFrom()}
                                    // maxDate={validateDateTo()}
                                    firstDayOfWeek={DayOfWeek.Monday}
                                    strings={DayPickerStrings}
                                    showWeekNumbers={true}
                                    formatDate={onFormatDate as any}
                                    disabled={!userCanUpdate()}
                                    initialPickerDate={
                                      new Date(proving.dateFrom)
                                    }
                                  />
                                )}
                              />
                              <FieldStackItem
                                name={`trackHotels.return.${i}.bookingStatus`}
                                render={(fieldProps: FieldProps<any>) => (
                                  <FormikDropdown
                                    label={t('field.bookingStatus.label')}
                                    {...fieldProps}
                                    options={statusOptions}
                                    disabled={!canUpdateStatus()}
                                  />
                                )}
                              />
                              <IconButton
                                styles={{
                                  root: { color: 'red', marginTop: 'auto' },
                                }}
                                disabled={!userCanUpdate()}
                                iconProps={{ iconName: 'Delete' }}
                                onClick={() => helpers.remove(i)}
                              />
                            </FieldStack>
                            <FieldStack>
                              <FieldStackItem
                                name={`trackHotels.return[${i}].note`}
                                label={t('section.proving.field.note')}
                                multiline={true}
                                component={FormikTextField}
                                disabled={!userCanUpdate()}
                              />
                            </FieldStack>
                          </FieldStack>
                        ))}
                        <FieldStack horizontal={true}>
                          <DefaultButton
                            text={'+ Hotel Rück'}
                            onClick={() => {
                              if (form.values.trackHotels.return.length < 10) {
                                helpers.push({
                                  hotel: '',
                                  dateFrom: '',
                                  dateTo: '',
                                  bookingStatus: BookingStatus.Open,
                                  note: '',
                                })
                              }
                            }}
                            disabled={
                              form.values.trackHotels.return.length >= 10 ||
                              !userCanUpdate()
                            }
                          />
                        </FieldStack>
                      </AutoStack>
                    )
                  }}
                </FieldArray>
              </FlexSpinner>
            </>
          )}
        {proving &&
          proving.provingGround &&
          !proving.provingGround.hideFerryConnection && (
            <>
              <StyledSectionTitle>
                <Text variant="xLarge" as="h2">
                  {t('field.ferryConnection.header.label')}
                </Text>
              </StyledSectionTitle>
              <>
                <FieldStackItem
                  name={`ferry.bookingStatus`}
                  render={(fieldProps: FieldProps<any>) => (
                    <FormikDropdown
                      label={t('field.bookingStatus.label')}
                      {...fieldProps}
                      options={statusOptions}
                      disabled={!canUpdateStatus()}
                    />
                  )}
                />
                <FieldStack horizontal={true}>
                  <FieldStackItem
                    name="ferry.outward"
                    render={(
                      fieldProps: FieldProps<ProvingDetailsFerryFormValues>
                    ) => (
                      <FormikDropdown
                        label={t('field.ferryConnection.outward.label')}
                        {...fieldProps}
                        options={[
                          { key: '', text: '' },
                          ...ferryConnectionsOutward.map(
                            (ferryConnectionOutward: any) =>
                              ({
                                key: ferryConnectionOutward._id,
                                text: ferryConnectionOutward.title,
                              } as IDropdownOption)
                          ),
                        ]}
                        onChanged={(option) => {
                          if (option.key === '') {
                            setSelectedFerryType(option.key)
                            resetOutward(fieldProps)
                          } else {
                            setOutward(fieldProps)
                          }
                        }}
                        disabled={!userCanUpdate()}
                      />
                    )}
                  />
                  <FieldStackItem
                    name="ferry.dateFrom"
                    label={t('field.ferryConnection.dateFrom.label')}
                    component={FormikGraphQLDatePicker}
                    // minDate={validateDateFrom()}
                    // maxDate={validateDateTo()}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={DayPickerStrings}
                    showWeekNumbers={true}
                    formatDate={onFormatDate}
                    disabled={!userCanUpdate()}
                    initialPickerDate={new Date(proving.dateFrom)}
                  />
                  <Stack>
                    <Label>{t('field.ferryConnection.timeFrom.label')}</Label>
                    <Stack.Item>
                      <TimePicker
                        component="input"
                        type="time"
                        name="ferry.timeFrom"
                        disabled={!userCanUpdate()}
                      />
                    </Stack.Item>
                  </Stack>

                  <FieldStackItem
                    name="resetFerryFrom"
                    render={(
                      fieldProps: FieldProps<ProvingDetailsFerryFormValues>
                    ) => (
                      <>
                        <Label>löschen</Label>
                        <IconButton
                          iconProps={{ iconName: 'Clear' }}
                          title="löschen"
                          onClick={() => resetOutward(fieldProps)}
                          disabled={!userCanUpdate()}
                        />
                      </>
                    )}
                  />
                </FieldStack>
                <FieldStack horizontal={true}>
                  <FieldStackItem
                    name="ferry.return"
                    render={(
                      fieldProps: FieldProps<ProvingDetailsFerryFormValues>
                    ) => (
                      <FormikDropdown
                        label={t('field.ferryConnection.return.label')}
                        {...fieldProps}
                        options={[
                          { key: '', text: '' },
                          ...ferryConnectionsReturn.map(
                            (ferryConnectionReturn: any) =>
                              ({
                                key: ferryConnectionReturn._id,
                                text: ferryConnectionReturn.title,
                              } as IDropdownOption)
                          ),
                        ]}
                        onChanged={(option) => {
                          if (option.key === '') {
                            setSelectedFerryType(option.key)
                            resetReturn(fieldProps)
                          } else {
                            setReturn(fieldProps)
                          }
                        }}
                        disabled={!userCanUpdate()}
                      />
                    )}
                  />
                  <FieldStackItem
                    name="ferry.dateTo"
                    label={t('field.ferryConnection.dateTo.label')}
                    component={FormikGraphQLDatePicker}
                    // minDate={validateDateFrom()}
                    // maxDate={validateDateTo()}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={DayPickerStrings}
                    showWeekNumbers={true}
                    formatDate={onFormatDate}
                    disabled={!userCanUpdate()}
                    initialPickerDate={new Date(proving.dateFrom)}
                  />
                  <Stack>
                    <Label>{t('field.ferryConnection.timeTo.label')}</Label>
                    <Stack.Item>
                      <TimePicker
                        component="input"
                        type="time"
                        name="ferry.timeTo"
                        disabled={!userCanUpdate()}
                      />
                    </Stack.Item>
                  </Stack>
                  <FieldStackItem
                    name="resetFerryTo"
                    render={(
                      fieldProps: FieldProps<ProvingDetailsFerryFormValues>
                    ) => (
                      <>
                        <Label>löschen</Label>
                        <IconButton
                          iconProps={{ iconName: 'Clear' }}
                          title="löschen"
                          onClick={() => resetReturn(fieldProps)}
                          disabled={!userCanUpdate()}
                        />
                      </>
                    )}
                  />
                </FieldStack>
                <FieldStack>
                  <Field
                    name="ferry.note"
                    label={t('section.proving.field.note')}
                    multiline={true}
                    component={FormikTextField}
                    disabled={!userCanUpdate()}
                  />
                </FieldStack>
              </>
            </>
          )}
      </FormStack>
      {initialValues.changedBy && initialValues.changeTimeStamp && (
        <>
          <br />
          <Text2 color="neutralSecondary">
            Zuletzt bearbeitet von{' '}
            {initialValues.changedBy && initialValues.changedBy.name}
            &nbsp;
            <TimeAgo date={initialValues.changeTimeStamp} />
          </Text2>
        </>
      )}
    </>
  )
}
